import { useState } from 'react';
import { Play, FileText, Music, Download, ExternalLink, X } from 'lucide-react';
import { Button } from './ui/button';
import {
  isGoogleDriveUrl,
  getGoogleDriveEmbedUrl,
  getGoogleDriveDirectUrl,
  extractGoogleDriveFileId,
} from '../utils/googleDrive';

/**
 * Modal viewer for media content
 * Supports video, PDF, and audio from Google Drive or direct URLs
 */
const MediaViewer = ({ url, type, title, isOpen, onClose }) => {
  const [loading, setLoading] = useState(true);

  if (!isOpen) return null;

  const isGDrive = isGoogleDriveUrl(url);
  const embedUrl = isGDrive ? getGoogleDriveEmbedUrl(url) : url;
  const directUrl = isGDrive ? getGoogleDriveDirectUrl(url) : url;
  const fileId = extractGoogleDriveFileId(url);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      onClick={handleBackdropClick}
    >
      <div className="relative w-full max-w-5xl bg-card rounded-2xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="text-lg font-bold truncate pr-4" style={{ color: '#ffffff' }}>
            {title}
          </h3>
          <div className="flex items-center gap-2">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
              title="Open in new tab"
            >
              <ExternalLink size={20} style={{ color: '#D4AF37' }} />
            </a>
            {type !== 'video' && (
              <a
                href={directUrl}
                download
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
                title="Download"
              >
                <Download size={20} style={{ color: '#D4AF37' }} />
              </a>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
              title="Close"
            >
              <X size={20} style={{ color: '#ffffff' }} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37] mx-auto mb-3"></div>
                <p className="text-sm text-muted-foreground">Loading content...</p>
              </div>
            </div>
          )}

          {type === 'video' && (
            <div className="aspect-video bg-black">
              <iframe
                src={embedUrl}
                className="w-full h-full"
                allow="autoplay; encrypted-media; fullscreen"
                allowFullScreen
                title={title}
                onLoad={() => setLoading(false)}
              />
            </div>
          )}

          {type === 'pdf' && (
            <div className="h-[70vh] bg-white">
              <iframe
                src={embedUrl}
                className="w-full h-full"
                title={title}
                onLoad={() => setLoading(false)}
              />
            </div>
          )}

          {type === 'audio' && (
            <div className="p-12 flex flex-col items-center gap-6 bg-gradient-to-b from-black/50 to-transparent">
              <div className="w-32 h-32 rounded-full flex items-center justify-center" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
                <Music size={64} style={{ color: '#D4AF37' }} />
              </div>
              <audio
                controls
                autoPlay
                className="w-full max-w-lg"
                onLoadedData={() => setLoading(false)}
                onCanPlay={() => setLoading(false)}
              >
                <source src={isGDrive ? `https://drive.google.com/uc?export=download&id=${fileId}` : url} />
                Your browser does not support the audio element.
              </audio>
              <p className="text-xs text-muted-foreground text-center">
                {isGDrive ? 'Streaming from Google Drive' : 'Playing from external source'}
              </p>
            </div>
          )}

          {type === 'quiz' && (
            <div className="h-[70vh] bg-white">
              <iframe
                src={url}
                className="w-full h-full"
                title={title}
                allow="fullscreen"
                onLoad={() => setLoading(false)}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border">
          <p className="text-xs text-muted-foreground truncate">
            Source: {isGDrive ? `Google Drive (${fileId})` : url}
          </p>
        </div>
      </div>
    </div>
  );
};

export default MediaViewer;
